import React, { useState, useCallback, useEffect, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FragnoButton } from 'fragno/react';
import { FragnoDropdown } from 'fragno/react';
import style from '../../../style/components/profile/emails/AddEmailRow.module.scss';
import DomainClassificationBadge from './DomainClassificationBadge';
import { USE_FOR_OPTIONS } from '../../../constants/emailOptions';

// Element id used to wire the error WalletAlert (rendered by the container)
// to the email input via aria-describedby. Keeping it as a module constant
// keeps the two sides in sync without exporting from the container.
const ADD_EMAIL_ERROR_ID = 'add-email-error';
// AXP-1630: ids for the inline "required" errors below each field.
const ADD_EMAIL_REQUIRED_ERROR_ID = 'add-email-required-error';
const ADD_USE_FOR_REQUIRED_ERROR_ID = 'add-use-for-required-error';

// errorCodes that require the user to edit the address before retrying.
// Any errorCode not in this set (including network / 5xx / unknown codes)
// is treated as potentially transient — the user can re-submit the same
// address. Exported so the container can apply the same gate to the
// SuspiciousDomainPrompt Continue button (the unknown-domain flow also
// dispatches addEmail and can hit the same VALIDATION_ERROR path).
// AXP-1613: ALREADY_EXISTS can no longer come from addEmail — a collision is
// never revealed until OTP succeeds (see EmailCollisionAlert), regardless of
// whether the entity was already created server-side for this address.
// ALREADY_PENDING and ALREADY_VERIFIED (this wallet already has this
// address, unconfirmed or confirmed) are included here too — resubmitting
// the identical address changes nothing server-side, so it would just hit
// the same error again.
const REQUIRES_EDIT_ERROR_CODES = new Set([
  'INVALID_EMAIL',
  'VALIDATION_ERROR',
  'ALREADY_PENDING',
  'ALREADY_VERIFIED',
  'ADD_EMAIL_RATE_LIMITED',
  'ADD_EMAIL_LOCKED',
]);

export const requiresInputEdit = (addError) =>
  !!addError &&
  (addError.source === 'validate' ||
    REQUIRES_EDIT_ERROR_CODES.has(addError.errorCode));

const AddEmailRow = ({
  onValidate,
  onSubmit,
  onCancel,
  onClearClassification,
  onClearAddError,
  classification,
  domain,
  isSubmitting,
  isValidating,
  addError,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [useFor, setUseFor] = useState('');
  // AXP-1630: Submit is always clickable now — these track which
  // required field(s) were empty on the last attempted submit, so the
  // error shows inline next to that field instead of just disabling
  // the button. Cleared as soon as the user fills in the field.
  const [emailRequiredError, setEmailRequiredError] = useState(false);
  const [useForRequiredError, setUseForRequiredError] = useState(false);
  const emailInputRef = useRef(null);
  // AXP-1641: captures { address, useFor } when handleSubmit is invoked
  // while a validate is still in flight (isValidating), instead of the
  // click being silently swallowed — the Submit button used to disable on
  // isValidating, and clicking it also blurs the input first, which kicks
  // off that same validate. The click landed inside the same render pass
  // that disabled the button, so it never registered. The effect below
  // fires the deferred submit once validate resolves, re-checking gating
  // fresh. Cleared on any edit so a stale submit can't fire for text the
  // user already changed.
  const pendingSubmitRef = useRef(null);

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  // Submit is gated when the active addError points at a problem the user
  // can only fix by editing the address. Two cases:
  //  - Any validate-source failure (source='validate'): VALIDATE_EMAIL_BEGIN
  //    clears `classification`, so a failed validate leaves us without the
  //    signal handleSubmitAdd routes on. We refuse submit until the user
  //    edits and re-blurs to re-run validate — this includes transient
  //    network / 5xx failures on the validate endpoint.
  //  - addEmail-source failures with a known errorCode (INVALID_EMAIL,
  //    VALIDATION_ERROR): the server told us the address is wrong;
  //    re-submitting won't help.
  // Transient addEmail-source failures (5xx, network, unknown errorCode)
  // fall through to the generic message AND leave submit enabled — those
  // are presumed retryable.
  const submitGatedByError = requiresInputEdit(addError);

  const handleEmailChange = useCallback(
    (e) => {
      setEmail(e.target.value);
      // Any edit invalidates a submit that was waiting on the in-flight
      // validate for the previous text (see pendingSubmitRef above).
      pendingSubmitRef.current = null;
      if (e.target.value.trim()) {
        setEmailRequiredError(false);
      }
      if (classification) {
        onClearClassification();
      }
      if (addError && onClearAddError) {
        onClearAddError();
      }
    },
    [classification, onClearClassification, addError, onClearAddError],
  );

  const handleEmailBlur = useCallback(() => {
    const trimmed = email.trim();
    // AXP-1641: validate on blur for any non-empty value — the format
    // decision (including obviously-malformed input) now happens
    // immediately, client-side, inside the validateEmail thunk itself.
    if (trimmed) {
      onValidate(trimmed);
    }
  }, [email, onValidate]);

  const handleUseForChange = useCallback((newValue) => {
    setUseFor(newValue);
    if (newValue) {
      setUseForRequiredError(false);
    }
  }, []);

  const handleSubmit = useCallback(() => {
    // Defensive guard mirrors the FragnoButton `disabled` prop below. If
    // FragnoButton ever maps `disabled` to `aria-disabled` only (visual /
    // semantic) rather than the native `disabled` attribute, click events
    // would still propagate — re-checking here keeps the gate honest.
    if (isSubmitting || submitGatedByError) return;

    // AXP-1630: Submit is always clickable — required-field checks moved
    // here (inline errors) instead of gating the button's disabled state.
    const trimmed = email.trim();
    const emailMissing = !trimmed;
    const useForMissing = !useFor;
    setEmailRequiredError(emailMissing);
    setUseForRequiredError(useForMissing);
    if (emailMissing || useForMissing) return;

    // AXP-1641: a validate is already in flight (e.g. kicked off by this
    // same click, which blurs the input before the click is processed) —
    // defer rather than drop. The effect below fires it once validate
    // resolves.
    if (isValidating) {
      pendingSubmitRef.current = { address: trimmed, useFor };
      return;
    }
    onSubmit(trimmed, useFor);
  }, [email, useFor, onSubmit, isSubmitting, isValidating, submitGatedByError]);

  // Fires a submit that was deferred because a validate was in flight at
  // click time. Re-checks gating fresh and only fires if the address/useFor
  // still match what was captured — handleEmailChange clears the ref on any
  // edit, but this is a second guard against firing for stale text.
  useEffect(() => {
    if (isValidating) return;
    const pending = pendingSubmitRef.current;
    if (!pending) return;
    pendingSubmitRef.current = null;

    if (isSubmitting || submitGatedByError) return;
    if (pending.address === email.trim() && pending.useFor === useFor) {
      onSubmit(pending.address, pending.useFor);
    }
  }, [isValidating, isSubmitting, submitGatedByError, email, useFor, onSubmit]);

  const handleCancel = useCallback(() => {
    setEmail('');
    setUseFor('');
    setEmailRequiredError(false);
    setUseForRequiredError(false);
    onCancel();
  }, [onCancel]);

  const options = USE_FOR_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(opt.label),
  }));

  // See EmailRow.jsx for the rationale behind `data-mobile-label` + the
  // `.mobileLabel` <span>. The email cell wraps its input + classification
  // badge in a `.cellValue` div so the < 780px grid layout sees a single
  // value item (otherwise auto-flow puts the badge into the label column).
  return (
    <tr className={style.row}>
      <td colSpan="2" className={style.cell} data-mobile-label>
        <span className={style.mobileLabel}>{t('walletEmails.email')}</span>
        <div className={style.cellValue}>
          <input
            ref={emailInputRef}
            type="email"
            className={`${style.emailInput} ${
              classification === 'malicious' || addError || emailRequiredError
                ? style.maliciousInput
                : classification === 'unknown'
                ? style.unknownInput
                : style.defaultInput
            }`}
            placeholder={t('walletEmails.addEmailPlaceholder')}
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            disabled={isSubmitting}
            aria-label={t('walletEmails.addEmailPlaceholder')}
            aria-invalid={
              !!addError || emailRequiredError || classification === 'malicious'
            }
            aria-describedby={
              addError
                ? ADD_EMAIL_ERROR_ID
                : emailRequiredError
                ? ADD_EMAIL_REQUIRED_ERROR_ID
                : undefined
            }
          />
          {emailRequiredError && (
            <span
              id={ADD_EMAIL_REQUIRED_ERROR_ID}
              className={style.fieldError}
              role="alert"
            >
              {t('walletEmails.addEmailFieldRequired', {
                field: t('walletEmails.email'),
              })}
            </span>
          )}
          <DomainClassificationBadge
            classification={classification}
            domain={domain}
          />
        </div>
      </td>
      <td className={style.cell} data-mobile-label>
        <span className={style.mobileLabel}>{t('walletEmails.useFor')}</span>
        <div className={style.dropdownWrapper}>
          <FragnoDropdown
            data-testid="add-email-use-for"
            options={options}
            value={useFor}
            placeholder={t('walletEmails.selectUse')}
            onChange={handleUseForChange}
          />
          {/* AXP-1630: FragnoDropdown has no error/aria-describedby prop to
              wire this to directly (not a Fragno slot/attribute today) — the
              role="alert" below still gets announced on appearance, just
              without a formal description association to the dropdown. */}
          {useForRequiredError && (
            <span
              id={ADD_USE_FOR_REQUIRED_ERROR_ID}
              className={style.fieldError}
              role="alert"
            >
              {t('walletEmails.addEmailFieldRequired', {
                field: t('walletEmails.useFor'),
              })}
            </span>
          )}
        </div>
      </td>
      <td colSpan="2" className={style.actionCell}>
        <div className={style.actionGroup}>
          <FragnoButton
            onClick={handleSubmit}
            disabled={isSubmitting || submitGatedByError}
            hierarchy="secondary"
            label={
              isSubmitting
                ? t('walletEmails.addEmailSubmitting')
                : t('walletEmails.addEmailSubmit')
            }
            size="md"
          />
          <button
            type="button"
            className={style.cancelLink}
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            {t('walletEmails.addEmailCancel')}
          </button>
        </div>
      </td>
    </tr>
  );
};

AddEmailRow.propTypes = {
  onValidate: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onClearClassification: PropTypes.func.isRequired,
  onClearAddError: PropTypes.func,
  classification: PropTypes.oneOf(['personal', 'work', 'unknown', 'malicious']),
  domain: PropTypes.string,
  isSubmitting: PropTypes.bool,
  isValidating: PropTypes.bool,
  addError: PropTypes.shape({
    source: PropTypes.string,
    errorCode: PropTypes.string,
    error: PropTypes.string,
  }),
};

AddEmailRow.defaultProps = {
  classification: null,
  domain: null,
  isSubmitting: false,
  isValidating: false,
  addError: null,
  onClearAddError: null,
};

export default memo(AddEmailRow);
export { AddEmailRow as AddEmailRowUnmemoized, ADD_EMAIL_ERROR_ID };
